// components/shared/CrearPersonaModal.tsx
import React, { useEffect, useState } from 'react';
import { PersonaSQL } from '../../types/PersonaSQL';
import { postPersona } from '../../services/General/personaService';
import { useAuth } from '../../context/AuthContext';

interface CrearPersonaModalProps {
    show: boolean;
    onHide: () => void;
    searchQuery: string;
    onPersonaCreada: (persona: PersonaSQL) => void;
}

const CrearPersonaModal: React.FC<CrearPersonaModalProps> = ({
    show,
    onHide,
    searchQuery,
    onPersonaCreada
}) => {
    const [formData, setFormData] = useState({
        nombre: '',
        apellido1: '',
        apellido2: '',
        tipo_identificacion: 'CEDULA' as 'CEDULA' | 'DIMEX' | 'PASAPORTE', // Tipado correcto
        identificador: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { user } = useAuth();

    // Función CORREGIDA: tipado correcto
    const analizarTextoBusqueda = (texto: string) => {
        if (!texto.trim()) {
            return {
                posibleIdentificador: '',
                tipoSugerido: 'CEDULA' as 'CEDULA' | 'DIMEX' | 'PASAPORTE',
                nombre: '',
                apellido1: '',
                apellido2: ''
            };
        }

        const partes = texto.trim().split(/\s+/);
        let posibleIdentificador = '';
        let restoTexto = texto;

        // Detectar si el primer token es un identificador
        const primerToken = partes[0];
        const soloNumeros = /^\d+$/;
        const posiblePasaporte = /^[A-Z0-9]{6,12}$/i;

        if (soloNumeros.test(primerToken) || posiblePasaporte.test(primerToken)) {
            posibleIdentificador = primerToken;
            restoTexto = partes.slice(1).join(' ');
        }

        // 🔥 CORREGIDO: Tipo explícito con unión
        let tipoSugerido: 'CEDULA' | 'DIMEX' | 'PASAPORTE' = 'CEDULA';

        if (posibleIdentificador) {
            if (/^\d{9}$/.test(posibleIdentificador)) {
                tipoSugerido = 'CEDULA';
            } else if (/^\d{12}$/.test(posibleIdentificador)) {
                tipoSugerido = 'DIMEX';
            } else if (/^[A-Z0-9]{6,12}$/i.test(posibleIdentificador)) {
                tipoSugerido = 'PASAPORTE';
            }
        }

        // Analizar el resto como nombre (si existe)
        let nombre = '';
        let apellido1 = '';
        let apellido2 = '';

        if (restoTexto.trim()) {
            const nombrePartes = restoTexto.trim().split(/\s+/);

            if (nombrePartes.length === 1) {
                nombre = nombrePartes[0];
            } else if (nombrePartes.length === 2) {
                nombre = nombrePartes[0];
                apellido1 = nombrePartes[1];
            } else if (nombrePartes.length === 3) {
                nombre = nombrePartes[0];
                apellido1 = nombrePartes[1];
                apellido2 = nombrePartes[2];
            } else {
                const mitad = Math.ceil(nombrePartes.length / 2);
                nombre = nombrePartes.slice(0, mitad).join(' ');
                apellido1 = nombrePartes[mitad] || '';
                apellido2 = nombrePartes.slice(mitad + 1).join(' ') || '';
            }
        }

        return {
            posibleIdentificador: posibleIdentificador || '',
            tipoSugerido,
            nombre: nombre.toUpperCase() || '',
            apellido1: apellido1.toUpperCase() || '',
            apellido2: apellido2.toUpperCase() || ''
        };
    };

    useEffect(() => {
        if (searchQuery && show) {
            const analisis = analizarTextoBusqueda(searchQuery);

            setFormData({
                identificador: analisis.posibleIdentificador,
                tipo_identificacion: analisis.tipoSugerido,
                nombre: analisis.nombre,
                apellido1: analisis.apellido1,
                apellido2: analisis.apellido2
            });
        }
    }, [searchQuery, show]);

    const validarCampos = () => {
        if (!formData.identificador.trim()) {
            setError('La cédula/identificador es obligatorio');
            return false;
        }
        if (!formData.nombre.trim()) {
            setError('El nombre es obligatorio');
            return false;
        }
        if (!formData.apellido1.trim()) {
            setError('El primer apellido es obligatorio');
            return false;
        }

        // Validar formato según tipo
        const identificador = formData.identificador.trim();
        switch (formData.tipo_identificacion) {
            case 'CEDULA':
                if (!/^\d{9}$/.test(identificador)) {
                    setError('La cédula debe tener 9 dígitos numéricos');
                    return false;
                }
                break;
            case 'DIMEX':
                if (!/^\d{12}$/.test(identificador)) {
                    setError('El DIMEX debe tener 12 dígitos numéricos');
                    return false;
                }
                break;
            case 'PASAPORTE':
                if (!/^[A-Z0-9]{6,12}$/i.test(identificador)) {
                    setError('El pasaporte debe tener entre 6 y 12 caracteres alfanuméricos');
                    return false;
                }
                break;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validarCampos()) return;

        setLoading(true);
        setError('');

        try {
            const nuevaPersona: PersonaSQL = {
                identificador: formData.identificador.toUpperCase(),
                tipo_identificacion: formData.tipo_identificacion,
                nombre: formData.nombre.toUpperCase(),
                apellido1: formData.apellido1.toUpperCase(),
                apellido2: formData.apellido2.toUpperCase(),
                user: user?.User.email || '',
            };

            // Aquí iría tu llamada API real
            const response = await postPersona(nuevaPersona);
            if (!response.success) {
                const errorMsg = response.message || 'Error al crear la persona'
                setError(errorMsg);
                return
            }
            console.log('Respuesta de crear persona', response)
            // ✅ La persona se creó exitosamente
            // response.data contiene el registro creado (con id, fechas, etc.)

            // 1. Notificar al padre con la persona creada
            // El padre recibirá este objeto y actualizará el formulario de cita
            onPersonaCreada(response.data);

            // 2. Cerrar el modal
            onHide();

            // 3. Opcional: Mostrar mensaje de éxito
            // Se Podría mostrar un toast o alerta aquí, pero usualmente
            // el padre mostrará el mensaje cuando reciba la persona

        } catch (err: any) {
            console.log('Error al crear persona: ',err)
            setError(err.response.data.message || 'Error al crear la persona');
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Crear Nueva Persona</h5>
                        <button type="button" className="btn-close" onClick={onHide}></button>
                    </div>
                    <div className="modal-body">
                        {searchQuery && (
                            <div className="alert alert-info alert-sm py-1 mb-3">
                                <small>
                                    <i className="bi bi-info-circle me-1"></i>
                                    Basado en tu búsqueda: hemos pre-llenado los campos sugeridos.
                                    {!formData.identificador &&
                                        " No detectamos un número de identificación, por favor ingrésalo."}
                                </small>
                            </div>
                        )}

                        <form>
                            <div className="mb-3">
                                <label className="form-label">Tipo de Identificación</label>
                                <select
                                    className="form-select"
                                    value={formData.tipo_identificacion}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        tipo_identificacion: e.target.value as 'CEDULA' | 'DIMEX' | 'PASAPORTE'
                                    })}
                                >
                                    <option value="CEDULA">Cédula de Identidad (9 dígitos)</option>
                                    <option value="DIMEX">DIMEX (12 dígitos)</option>
                                    <option value="PASAPORTE">Pasaporte (6-12 caracteres)</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Número de Identificación</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.identificador}
                                    onChange={(e) => setFormData({ ...formData, identificador: e.target.value.toUpperCase() })}
                                    placeholder={
                                        formData.tipo_identificacion === 'CEDULA' ? 'Ej: 107400523' :
                                            formData.tipo_identificacion === 'DIMEX' ? 'Ej: 123456789012' :
                                                'Ej: A12345678'
                                    }
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Nombre</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                                    placeholder="Ej: GERARDO ADOLFO"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Primer Apellido</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.apellido1}
                                    onChange={(e) => setFormData({ ...formData, apellido1: e.target.value.toUpperCase() })}
                                    placeholder="Ej: NAJERA"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Segundo Apellido</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.apellido2}
                                    onChange={(e) => setFormData({ ...formData, apellido2: e.target.value.toUpperCase() })}
                                    placeholder="Ej: PICADO (opcional)"
                                />
                            </div>

                            {error && <div className="alert alert-danger">{error}</div>}
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Creando...
                                </>
                            ) : 'Crear Persona'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CrearPersonaModal;